import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  PortalDsAlert,
  PortalDsButton,
  portalDs,
} from 'src/components/portal-design-system';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import { cn } from 'src/lib/utils';
import type { DataJobRunDetail } from '../types/data-jobs.types';
import type { InflationFixSuggestion } from '../types/operations.types';
import { getInflationFixesFromRun } from '../utils/inflation-fixes';
import { DataJobStatusBadgeV2 } from './data-job-status-badge-v2';
import { DataJobStepResultsTable } from './data-job-step-results-table';
import { ValidationResultsMatrix } from './validation-results-matrix';
import { ValidationResultsTable } from './validation-results-table';

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) {
    return '—';
  }
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}m ${remainder}s`;
}

interface DataJobRunSheetV2Props {
  open: boolean;
  run: DataJobRunDetail | null | undefined;
  isLoading?: boolean;
  isRerunning?: boolean;
  isFixingInflation?: boolean;
  rerunError?: string | null;
  inflationFixError?: string | null;
  inflationFixSuccess?: string | null;
  onOpenChange: (open: boolean) => void;
  onRerun?: () => void;
  onFixInflation?: (fix: InflationFixSuggestion) => void;
}

export function DataJobRunSheetV2({
  open,
  run,
  isLoading = false,
  isRerunning = false,
  isFixingInflation = false,
  rerunError = null,
  inflationFixError = null,
  inflationFixSuccess = null,
  onOpenChange,
  onRerun,
  onFixInflation,
}: DataJobRunSheetV2Props) {
  const [pendingFixKey, setPendingFixKey] = useState<string | null>(null);
  const inflationFixes = useMemo(() => getInflationFixesFromRun(run), [run]);
  const canRerun = Boolean(
    run
    && ['success', 'warning', 'failed', 'interrupted'].includes(run.status)
    && onRerun
  );

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-y-auto bg-white p-0 sm:!w-[70vw] sm:!max-w-none"
      >
        <div className={portalDs.sheetHeader}>
          <SheetHeader className="space-y-1 text-left">
            <SheetTitle className={portalDs.sheetTitle}>
              {run?.job_label ?? 'Run detail'}
            </SheetTitle>
            <SheetDescription className={portalDs.sheetDescription}>
              Inspect parameters, step progress, and validation outcomes for this run.
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className="flex-1 space-y-8 px-6 py-5">
          {isLoading ? (
            <div
              className={cn('flex items-center gap-2 py-8', portalDs.bodySm)}
              role="status"
            >
              <Loader2 className="size-4 animate-spin text-[#9CA3AF]" />
              Loading run detail…
            </div>
          ) : null}

          {!isLoading && run ? (
            <>
              <section className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <DataJobStatusBadgeV2 status={run.status} />
                  <span className={cn(portalDs.bodySm, 'capitalize')}>
                    {run.trigger_source}
                  </span>
                  {run.triggered_by_display ? (
                    <span className={portalDs.bodySm}>
                      by
                      {' '}
                      {run.triggered_by_display}
                    </span>
                  ) : null}
                </div>

                <dl className="grid grid-cols-2 gap-4 text-sm">
                  {[
                    ['Active date', run.date_range ?? run.active_date ?? '—'],
                    ['Duration', formatDuration(run.duration_seconds)],
                    ['Created', formatDisplayDateTime(run.created_at)],
                    ['Finished', run.finished_at ? formatDisplayDateTime(run.finished_at) : '—'],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt className={portalDs.filterLabel}>
                        {label}
                      </dt>
                      <dd className="mt-1 text-[13.5px] text-[#0D0F1A]">
                        {value}
                      </dd>
                    </div>
                  ))}
                  <div className="col-span-2">
                    <dt className={portalDs.filterLabel}>
                      Summary
                    </dt>
                    <dd className="mt-1 whitespace-pre-wrap text-[13.5px] text-[#0D0F1A]">
                      {run.summary ?? run.error_summary ?? '—'}
                    </dd>
                  </div>
                </dl>

                {canRerun ? (
                  <div className="space-y-2">
                    <PortalDsButton
                      type="button"
                      variant="secondary"
                      onClick={onRerun}
                      disabled={isRerunning}
                    >
                      {isRerunning ? 'Starting rerun…' : 'Rerun'}
                    </PortalDsButton>
                    {rerunError ? (
                      <p
                        className="text-xs text-[#DC2626]"
                        role="alert"
                      >
                        {rerunError}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </section>

              {inflationFixes.length > 0 && onFixInflation ? (
                <section className={portalDs.sheetSection}>
                  <h3 className={portalDs.sheetSectionTitle}>
                    Suspected inflated load
                  </h3>
                  <p className={cn(portalDs.bodySm, 'mb-3')}>
                    Mart totals look like exact multiples of vendor totals — usually from raw
                    appends being summed into staging. Fix clears raw/staging for that date and
                    reloads ETL. Re-run Daily Verification after ETL finishes.
                  </p>
                  <div className="space-y-3">
                    {inflationFixes.map((fix) => {
                      const key = `${fix.platform}:${fix.active_date}`;
                      const confirming = pendingFixKey === key;
                      return (
                        <div
                          key={key}
                          className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3"
                        >
                          <p className="text-[13.5px] font-medium text-[#92400E]">
                            {fix.summary}
                          </p>
                          <p className={cn(portalDs.caption, 'mt-1 text-[#92400E]')}>
                            Accounts:
                            {' '}
                            {fix.accounts.join(', ') || '—'}
                          </p>
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            {!confirming ? (
                              <PortalDsButton
                                type="button"
                                variant="accent"
                                size="sm"
                                disabled={isFixingInflation}
                                onClick={() => setPendingFixKey(key)}
                              >
                                Fix
                                {' '}
                                {fix.platform}
                              </PortalDsButton>
                            ) : (
                              <>
                                <PortalDsButton
                                  type="button"
                                  variant="accent"
                                  size="sm"
                                  disabled={isFixingInflation}
                                  onClick={() => onFixInflation(fix)}
                                >
                                  {isFixingInflation ? 'Starting fix…' : 'Confirm clear + reload'}
                                </PortalDsButton>
                                <PortalDsButton
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  disabled={isFixingInflation}
                                  onClick={() => setPendingFixKey(null)}
                                >
                                  Cancel
                                </PortalDsButton>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {inflationFixError ? (
                    <PortalDsAlert
                      variant="danger"
                      title="Could not start fix"
                      className="mt-3"
                    >
                      {inflationFixError}
                    </PortalDsAlert>
                  ) : null}
                  {inflationFixSuccess ? (
                    <PortalDsAlert
                      variant="success"
                      title="Fix started"
                      className="mt-3"
                    >
                      {inflationFixSuccess}
                    </PortalDsAlert>
                  ) : null}
                </section>
              ) : null}

              <section className={portalDs.sheetSection}>
                <h3 className={portalDs.sheetSectionTitle}>
                  Parameters
                </h3>
                <pre className="overflow-x-auto rounded-lg border border-[#EDEEF2] bg-[#F7F8FA] p-3 font-mono text-xs text-[#252840]">
                  {JSON.stringify(run.parameters, null, 2)}
                </pre>
              </section>

              <section className={portalDs.sheetSection}>
                <h3 className={portalDs.sheetSectionTitle}>
                  Step results
                </h3>
                {run.step_runs.length ? (
                  <DataJobStepResultsTable steps={run.step_runs} />
                ) : (
                  <p className={portalDs.bodySm}>
                    No step results recorded yet.
                  </p>
                )}
              </section>

              <section className={portalDs.sheetSection}>
                <h3 className={portalDs.sheetSectionTitle}>
                  Validation results
                </h3>
                {run.validation_results.length ? (
                  run.job_key === 'daily_verification'
                    || run.job_key === 'mart_verification'
                    ? <ValidationResultsMatrix results={run.validation_results} />
                    : <ValidationResultsTable results={run.validation_results} />
                ) : (
                  <p className={portalDs.bodySm}>
                    No validation results recorded.
                  </p>
                )}
              </section>
            </>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
